import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../authentication_components/authContext';
import { usePrlocalBusinessData } from '../../authentication_components/prlocalBusinessDataContext';
import {
  Search, Calendar, Phone, Mail, Building, Loader2, AlertCircle, CheckCircle,
  XCircle, Filter, Download, RefreshCw, Edit2, X, Trash2, AlertTriangle, 
  Shield, ArrowLeft, Image, Package, Briefcase, CalendarDays, Share2, MapPin, Globe,
  Eye, EyeOff, Users, Clock
} from 'lucide-react';

// =================================================================
// 1. DeletionConfirmationDialog Component (Unchanged)
// =================================================================
const DeletionConfirmationDialog = ({ isOpen, onClose, business, onConfirmDelete, loading, error, successMessage }) => {
  const [confirmationInput, setConfirmationInput] = useState('');

  useEffect(() => {
    if (isOpen) {
      setConfirmationInput('');
    }
  }, [isOpen]);

  if (!isOpen || !business) return null;

  const isConfirmationValid = confirmationInput === business.business_registry_number;

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-75 flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full transform transition-all" onClick={e => e.stopPropagation()}>
        <div className="p-6 relative">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-50 mb-4">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">¿Está absolutamente seguro?</h3>
            <p className="text-slate-600 text-sm">Está a punto de eliminar permanentemente el negocio:</p>
            <p className="font-bold text-lg my-2 text-slate-800 break-words">{business.business_name}</p>
            <div className="my-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-900">
              Esta acción **no se puede deshacer**. Se borrarán todos los datos asociados.
            </div>
          </div>

          {!successMessage ? (
            <>
              <label className="block text-sm font-medium text-slate-700 mt-6">
                Para confirmar, escriba el número de registro: <br />
                <strong className="font-mono text-red-600 select-all">{business.business_registry_number}</strong>
              </label>
              <input
                type="text" value={confirmationInput}
                onChange={(e) => setConfirmationInput(e.target.value)}
                className="mt-1 w-full p-2 border border-slate-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500"
                autoComplete="off"
              />
              {error && <p className="text-red-600 text-sm mt-4 text-center">{error}</p>}
              <div className="flex space-x-3 mt-6">
                <button onClick={onClose} disabled={loading} className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors">
                  Cancelar
                </button>
                <button onClick={() => onConfirmDelete(confirmationInput)} disabled={!isConfirmationValid || loading} className="flex-1 flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-red-300 disabled:cursor-not-allowed">
                  {loading ? <Loader2 className="animate-spin h-5 w-5" /> : 'Eliminar'}
                </button>
              </div>
            </>
          ) : (
            <div className="mt-6 text-center">
              <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
              <p className="font-semibold text-emerald-800">Eliminación Exitosa</p>
              <p className="text-sm text-slate-600 mt-1">{successMessage}</p>
            </div>
          )}
        </div>
        {!loading && <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"><X className="h-6 w-6" /></button>}
      </div>
    </div>
  );
};

// =================================================================
// 2. VerificationManagementDialog Component (Unchanged)
// =================================================================
const VerificationManagementDialog = ({ isOpen, onClose, business, onVerificationUpdate }) => {
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && business) {
      setEmailVerified(business.verified_email || false);
      setPhoneVerified(business.verified_phone || false);
      setHasChanges(false);
      setSuccessMessage('');
    }
  }, [isOpen, business]);

  const handleEmailToggle = () => { setEmailVerified(!emailVerified); setHasChanges(true); };
  const handlePhoneToggle = () => { setPhoneVerified(!phoneVerified); setHasChanges(true); };

  const handleSaveChanges = async () => {
    if (!business || !hasChanges) return;
    setLoading(true);
    // Simulation
    setTimeout(() => {
      setSuccessMessage('Estado actualizado localmente (Simulado)');
      onVerificationUpdate(business.business_registry_number, emailVerified, phoneVerified);
      setLoading(false);
      setTimeout(onClose, 1500);
    }, 1000);
  };

  if (!isOpen || !business) return null;

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
            <Shield className="h-5 w-5 text-indigo-600" /> Gestionar Verificación
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><XCircle className="h-6 w-6" /></button>
        </div>
        <div className="p-6">
          <div className="mb-6">
            <h4 className="font-semibold text-slate-800">{business.business_name}</h4>
          </div>
          {successMessage ? (
            <div className="p-4 bg-emerald-50 text-emerald-800 rounded-lg flex gap-2"><CheckCircle className="h-5 w-5"/>{successMessage}</div>
          ) : (
            <div className="space-y-4">
              <div className="border p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2"><span className="font-medium">Email</span><Mail className="h-4 w-4 text-slate-400"/></div>
                <div className="flex justify-between items-center">
                   <span className="text-sm text-slate-600">{business.business_email}</span>
                   <button onClick={handleEmailToggle} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${emailVerified ? 'bg-emerald-600' : 'bg-slate-300'}`}>
                     <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${emailVerified ? 'translate-x-6' : 'translate-x-1'}`} />
                   </button>
                </div>
              </div>
              <div className="border p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2"><span className="font-medium">Teléfono</span><Phone className="h-4 w-4 text-slate-400"/></div>
                <div className="flex justify-between items-center">
                   <span className="text-sm text-slate-600">{business.business_phone}</span>
                   <button onClick={handlePhoneToggle} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${phoneVerified ? 'bg-emerald-600' : 'bg-slate-300'}`}>
                     <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${phoneVerified ? 'translate-x-6' : 'translate-x-1'}`} />
                   </button>
                </div>
              </div>
              <button onClick={handleSaveChanges} disabled={!hasChanges || loading} className="w-full mt-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {loading ? <Loader2 className="animate-spin h-5 w-5 mx-auto"/> : 'Guardar Cambios'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// =================================================================
// 3. EmailChangeDialog Component (Unchanged)
// =================================================================
const EmailChangeDialog = ({ isOpen, onClose, business, onEmailUpdateSuccess }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl">
        <h3 className="text-lg font-bold mb-4">Actualizar Email</h3>
        <p className="text-sm text-slate-600 mb-4">Simulación de cambio de email para: {business.business_name}</p>
        <button onClick={onClose} className="px-4 py-2 bg-slate-200 rounded hover:bg-slate-300">Cerrar</button>
      </div>
    </div>
  );
};

// =================================================================
// 4. BusinessControlsDrawer (Side Panel) - MODIFIED
// =================================================================
const BusinessControlsDrawer = ({ business, onClose }) => {
  const { formatBusinessData, getProfileIdByBusinessId } = usePrlocalBusinessData();
  const [formattedBusiness, setFormattedBusiness] = useState(null);
  const [profileSettings, setProfileSettings] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // When business changes, fetch its specific public settings
  useEffect(() => {
    if (business) {
      setFormattedBusiness(formatBusinessData(business));
      setProfileSettings(null); // Reset prev data
      setFetchError(null);
      setLoadingProfile(true);

      getProfileIdByBusinessId(business.id)
        .then((data) => {
          setProfileSettings(data);
        })
        .catch((err) => {
          console.error("Error fetching public profile settings:", err);
          // If 404, it means no profile exists yet, which is valid info
          setFetchError(err.message.includes('not found') ? 'Perfil no configurado' : 'Error al cargar datos');
        })
        .finally(() => {
          setLoadingProfile(false);
        });
    }
  }, [business, formatBusinessData, getProfileIdByBusinessId]);

  if (!business) return null;

  // Configuration mapping for the boolean flags from DB to UI
  const sections = [
    { key: 'profile_public', label: 'Perfil General', icon: Building, color: 'violet' },
    { key: 'profile_images_public', label: 'Galería de Imágenes', icon: Image, color: 'pink' },
    { key: 'profile_products_public', label: 'Catálogo de Productos', icon: Package, color: 'blue' },
    { key: 'profile_services_public', label: 'Servicios Ofrecidos', icon: Briefcase, color: 'emerald' },
    { key: 'profile_events_public', label: 'Eventos', icon: CalendarDays, color: 'amber' },
    { key: 'profile_contact_public', label: 'Información de Contacto', icon: Share2, color: 'cyan' }, // Mapped to contact
    { key: 'profile_location_public', label: 'Ubicación Física', icon: MapPin, color: 'red' },
    { key: 'profile_schedule_public', label: 'Horario Laboral', icon: Clock, color: 'orange' },
    { key: 'profile_employees_public', label: 'Equipo / Empleados', icon: Users, color: 'indigo' },
  ];

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      {/* Blur Overlay */}
      <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm transition-opacity opacity-100 animate-in fade-in duration-200" onClick={onClose} />

      {/* The Panel */}
      <div className="relative w-full sm:w-[500px] bg-white shadow-2xl border-l border-slate-200 h-full transform transition-transform duration-300 ease-in-out animate-in slide-in-from-right" onClick={(e) => e.stopPropagation()}>
        <div className="h-full flex flex-col">
          
          {/* Header */}
          <div className="p-6 border-b border-slate-100 bg-gradient-to-r from-violet-50 to-indigo-50">
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h2 className="text-xl font-bold text-slate-800">Detalles del Negocio</h2>
                <p className="text-xs text-slate-500 font-mono mt-1">{business.business_registry_number}</p>
              </div>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-2 hover:bg-white rounded-full transition-all">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div>
              <h3 className="font-bold text-slate-900 text-lg">{business.business_name}</h3>
              <p className="text-sm text-slate-600 mt-1">{business.business_owner}</p>
              
              {/* Quick Info Chips */}
              <div className="flex flex-wrap gap-2 mt-3">
                 <span className={`px-2 py-0.5 rounded text-xs font-medium ${business.verified_email ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    Email: {business.verified_email ? 'Verificado' : 'Pendiente'}
                 </span>
                 <span className={`px-2 py-0.5 rounded text-xs font-medium ${business.verified_phone ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    Tel: {business.verified_phone ? 'Verificado' : 'Pendiente'}
                 </span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            
            {/* System Info */}
            <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Datos del Sistema</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">ID Interno:</span>
                  <span className="font-mono text-slate-800">{business.id}</span>
                </div>
                {profileSettings?.profile_id && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">Profile ID (Público):</span>
                    <span className="font-mono text-slate-800">{profileSettings.profile_id}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-600">Fecha Registro:</span>
                  <span className="text-slate-800">{formattedBusiness?.registrationDateFormatted}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Términos Aceptados:</span>
                  <span className={`font-medium ${business.business_terms ? 'text-emerald-600' : 'text-red-600'}`}>
                    {business.business_terms ? 'Sí' : 'No'}
                  </span>
                </div>
              </div>
            </div>

            {/* Public Profile Visibility Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Visibilidad Pública</h4>
                  <p className="text-xs text-slate-500 mt-1">Estado actual configurado por el usuario (Solo Lectura)</p>
                </div>
                {loadingProfile && <Loader2 className="h-4 w-4 animate-spin text-violet-500" />}
              </div>

              {loadingProfile ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => <div key={i} className="h-16 bg-slate-100 animate-pulse rounded-xl"></div>)}
                </div>
              ) : fetchError ? (
                <div className="p-4 bg-amber-50 text-amber-800 border border-amber-200 rounded-lg text-sm flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  {fetchError}
                </div>
              ) : profileSettings ? (
                <div className="space-y-3">
                  {sections.map((section) => {
                    const Icon = section.icon;
                    // Access the boolean from the fetched object
                    const isVisible = profileSettings[section.key]; 
                    
                    return (
                      <div key={section.key} className="bg-white border border-slate-200 rounded-xl p-3.5 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg bg-${section.color}-50`}>
                            <Icon className={`h-5 w-5 text-${section.color}-600`} />
                          </div>
                          <div>
                            <h5 className="font-semibold text-slate-800 text-sm">{section.label}</h5>
                            <p className="text-xs text-slate-500">
                               Columna: <span className="font-mono">{section.key}</span>
                            </p>
                          </div>
                        </div>
                        
                        {/* READ ONLY INDICATOR - No Toggle */}
                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border ${
                          isVisible 
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-100' 
                            : 'bg-slate-50 text-slate-500 border-slate-200'
                        }`}>
                          {isVisible ? (
                            <>
                              <Eye className="h-3.5 w-3.5" /> Visible
                            </>
                          ) : (
                            <>
                              <EyeOff className="h-3.5 w-3.5" /> Oculto
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>

            {/* Read-only warning */}
            <div className="mt-6 bg-blue-50 border border-blue-100 rounded-lg p-4">
              <div className="flex gap-2">
                <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-900 font-medium">Modo Administrador</p>
                  <p className="text-xs text-blue-700 mt-1">
                    No puede modificar la visibilidad pública desde aquí. Estos ajustes son controlados exclusivamente por el dueño del negocio desde su aplicación.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer - Close Button Only */}
          <div className="p-6 border-t border-slate-200 bg-slate-50">
            <button
              onClick={onClose}
              className="w-full px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-white transition-colors font-medium shadow-sm"
            >
              Cerrar Panel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =================================================================
// 5. ModernAdminDashboard Component (Main)
// =================================================================
export default function ModernAdminDashboard() {
  const { loginType } = useAuth();
  const { getBusinesses, exportBusinessData, loading: contextLoading, error: contextError, clearError } = usePrlocalBusinessData();

  const [searchTerm, setSearchTerm] = useState('');
  const [businesses, setBusinesses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [localError, setLocalError] = useState(null);

  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showEmailChangeDialog, setShowEmailChangeDialog] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [businessToDelete, setBusinessToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [filters, setFilters] = useState({ category: '', verifiedEmail: null, verifiedPhone: null, dateFrom: '', dateTo: '' });
  const itemsPerPage = 10;
  const loading = contextLoading;
  const error = contextError || localError;

  const loadBusinesses = useCallback(async (page = 1, search = searchTerm) => {
    const hasActiveFilters = filters.category || filters.verifiedEmail !== null || filters.verifiedPhone !== null || filters.dateFrom || filters.dateTo;
    const hasSearchTerm = search.trim();
    
    if (!hasSearchTerm && !hasActiveFilters) {
      setBusinesses([]);
      setTotalCount(0);
      setHasSearched(false);
      return;
    }
    
    setLocalError(null);
    clearError();
    
    try {
      setHasSearched(true);
      const fetchLimit = hasSearchTerm ? 500 : itemsPerPage;
      const options = { limit: fetchLimit, offset: 0 };
      if (filters.category) options.category = filters.category;

      const result = await getBusinesses(options);
      let processedBusinesses = result.businesses || [];
      
      if (hasSearchTerm) {
        const searchLower = search.toLowerCase();
        processedBusinesses = processedBusinesses.filter(b =>
          b.business_name?.toLowerCase().includes(searchLower) ||
          b.business_owner?.toLowerCase().includes(searchLower) ||
          b.business_email?.toLowerCase().includes(searchLower) ||
          b.business_phone?.includes(search) ||
          b.business_registry_number?.toLowerCase().includes(searchLower) ||
          b.business_category?.toLowerCase().includes(searchLower)
        );
      }
      
      if (filters.verifiedEmail !== null) processedBusinesses = processedBusinesses.filter(b => b.verified_email === filters.verifiedEmail);
      if (filters.verifiedPhone !== null) processedBusinesses = processedBusinesses.filter(b => b.verified_phone === filters.verifiedPhone);
      if (filters.dateFrom) processedBusinesses = processedBusinesses.filter(b => new Date(b.registration_date) >= new Date(filters.dateFrom));
      if (filters.dateTo) {
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999);
        processedBusinesses = processedBusinesses.filter(b => new Date(b.registration_date) <= toDate);
      }

      processedBusinesses.sort((a, b) => new Date(b.business_created_at) - new Date(a.business_created_at));

      const totalFiltered = processedBusinesses.length;
      const startIndex = (page - 1) * itemsPerPage;
      const paginatedResults = processedBusinesses.slice(startIndex, startIndex + itemsPerPage);

      setBusinesses(paginatedResults);
      setTotalCount(totalFiltered);
      setCurrentPage(page);
    } catch (err) {
      setLocalError(err.message || 'Error al cargar negocios');
      setBusinesses([]);
    }
  }, [searchTerm, filters, getBusinesses, clearError]);

  useEffect(() => {
    const timeoutId = setTimeout(() => { loadBusinesses(1, searchTerm); }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, loadBusinesses]);

  const handleBusinessClick = (business) => { setSelectedBusiness(business); };
  const closeBusinessDrawer = () => { setSelectedBusiness(null); };

  // Action Handlers
  const handleVerificationClick = (b, e) => { e.stopPropagation(); setSelectedBusiness(b); setShowVerificationDialog(true); };
  const handleEmailChangeClick = (b, e) => { e.stopPropagation(); setSelectedBusiness(b); setShowEmailChangeDialog(true); };
  const openDeleteModal = (b, e) => { e.stopPropagation(); setBusinessToDelete(b); setIsDeleteModalOpen(true); };

  // Filter UI Helpers
  const handleFilterChange = (k, v) => { setFilters(prev => ({ ...prev, [k]: v })); setCurrentPage(1); };
  const applyFilters = () => { setCurrentPage(1); loadBusinesses(1); setShowFilters(false); };
  const clearFilters = () => { setFilters({ category: '', verifiedEmail: null, verifiedPhone: null, dateFrom: '', dateTo: '' }); setCurrentPage(1); setBusinesses([]); setTotalCount(0); setHasSearched(false); };
  
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalCount);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50">
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col gap-4">
            <div className="relative w-full">
              <Search className="absolute inset-y-0 left-3 my-auto h-5 w-5 text-violet-400" />
              <input type="text" placeholder="Buscar negocios..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-12 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500" />
              {loading && <Loader2 className="absolute inset-y-0 right-3 my-auto h-4 w-4 animate-spin text-violet-500" />}
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"><Filter className="h-4 w-4"/> Filtros</button>
              {!hasSearched && <button onClick={() => setSearchTerm(' ')} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"><RefreshCw className="h-4 w-4"/> Cargar Todos</button>}
            </div>
          </div>
          
          {showFilters && (
             <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <input type="text" placeholder="Categoría" value={filters.category} onChange={e => handleFilterChange('category', e.target.value)} className="p-2 border rounded" />
                <div className="flex gap-2 col-span-full pt-2">
                   <button onClick={applyFilters} className="px-4 py-2 bg-violet-600 text-white rounded text-sm">Aplicar</button>
                   <button onClick={clearFilters} className="px-4 py-2 border rounded text-sm bg-white">Limpiar</button>
                </div>
             </div>
          )}
          
          {error && <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2"><AlertCircle className="h-5 w-5"/>{error}</div>}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-slate-800">{hasSearched ? `Resultados (${totalCount})` : 'Directorio de Negocios'}</h2>
            {totalCount > 0 && <span className="text-sm text-slate-600">Showing {startIndex}-{endIndex} of {totalCount}</span>}
          </div>
          
          {businesses.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-violet-50 to-indigo-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-indigo-900">Nombre</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-indigo-900">Propietario</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-indigo-900">Email</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-indigo-900">Estado</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-indigo-900">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {businesses.map((business) => (
                    <tr key={business.id} className="hover:bg-violet-50 transition-colors cursor-pointer" onClick={() => handleBusinessClick(business)}>
                      <td className="px-6 py-4 text-sm font-medium text-slate-900">{business.business_name}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{business.business_owner}</td>
                      <td className="px-6 py-4 text-sm text-violet-600">{business.business_email}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${business.verified_email && business.verified_phone ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                          {business.verified_email && business.verified_phone ? 'Verificado' : 'Pendiente'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                           <button onClick={(e) => { e.stopPropagation(); handleBusinessClick(business); }} className="p-2 text-blue-600 hover:bg-blue-100 rounded-full"><Globe className="h-4 w-4"/></button>
                           <button onClick={(e) => handleVerificationClick(business, e)} className="p-2 text-emerald-600 hover:bg-emerald-100 rounded-full"><Shield className="h-4 w-4"/></button>
                           <button onClick={(e) => handleEmailChangeClick(business, e)} className="p-2 text-violet-600 hover:bg-violet-100 rounded-full"><Edit2 className="h-4 w-4"/></button>
                           {loginType === 'master' && <button onClick={(e) => openDeleteModal(business, e)} className="p-2 text-red-600 hover:bg-red-100 rounded-full"><Trash2 className="h-4 w-4"/></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center text-slate-500">
               {loading ? <Loader2 className="h-10 w-10 mx-auto animate-spin mb-4"/> : <Search className="h-12 w-12 mx-auto mb-4 text-slate-300"/>}
               <h3 className="text-lg font-medium">No se encontraron negocios</h3>
               <p className="text-sm mt-2">{hasSearched ? 'Intente ajustar los filtros' : 'Use el buscador o cargue todos los datos'}</p>
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-slate-200 flex justify-between items-center bg-slate-50">
              <span className="text-sm text-slate-700">Página {currentPage} de {totalPages}</span>
              <div className="space-x-2">
                <button onClick={() => loadBusinesses(currentPage - 1)} disabled={currentPage === 1 || loading} className="px-4 py-2 border rounded-md bg-white hover:bg-slate-50 disabled:opacity-50">Anterior</button>
                <button onClick={() => loadBusinesses(currentPage + 1)} disabled={currentPage === totalPages || loading} className="px-4 py-2 border rounded-md bg-white hover:bg-slate-50 disabled:opacity-50">Siguiente</button>
              </div>
            </div>
          )}
        </div>
      </main>

      <BusinessControlsDrawer business={selectedBusiness} onClose={closeBusinessDrawer} />
      
      {selectedBusiness && (
        <>
          <VerificationManagementDialog isOpen={showVerificationDialog} onClose={() => {setShowVerificationDialog(false); setSelectedBusiness(null);}} business={selectedBusiness} onVerificationUpdate={() => {}} />
          <EmailChangeDialog isOpen={showEmailChangeDialog} onClose={() => {setShowEmailChangeDialog(false); setSelectedBusiness(null);}} business={selectedBusiness} onEmailUpdateSuccess={() => {}} />
        </>
      )}

      <DeletionConfirmationDialog isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} business={businessToDelete} onConfirmDelete={() => {}} loading={false} />
    </div>
  );
}